import { sanitizeHTML } from '../../../../safe-html.mjs';
import axios from '../../../api-client.ts';
import clsx from 'clsx';
import { Field, Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import Toast from 'react-bootstrap/Toast';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import parse from 'url-parse';
import * as Yup from 'yup';
import { ThemeModeComponent } from '../../../../_mesomb/assets/ts/layout';
import {
  KTCard,
  KTCardBody,
  toAbsoluteStatic,
} from '../../../../_mesomb/helpers';
import i18n from '../../../../_mesomb/i18n';
import ButtonIndicator from '../../../../_mesomb/partials/components/ButtonIndicator.tsx';
import FormField from '../../../../_mesomb/partials/form/FormField.tsx';
import { useThemeMode } from '../../../../_mesomb/partials/layout/theme-mode/ThemeModeProvider.tsx';
import {
  extract,
  getPaymentMethod,
  processErrorResponse,
  random,
} from '../../../../_mesomb/utils';
import { TrxSocket } from '../../../../_mesomb/utils/TrxSocket.ts';
import { FundModel, TransactionPerformResponse } from '../../../../types';
import FundTarget from '../../fund/components/FundTarget.tsx';

const formSchema = Yup.object().shape({
  payer: Yup.string()
    .required(i18n.t('Field_is_required'))
    .matches(/^[+]?\d{8,12}$/, i18n.t('Invalid phone number')),
  service: Yup.string().required(i18n.t('Please select a payment provider')),
  amount: Yup.number()
    .required(i18n.t('Field_is_required'))
    .min(1, i18n.t('Invalid amount')),
  accept_terms: Yup.boolean().required(i18n.t('Field_is_required')),
  anonymous: Yup.boolean().default(false),
  hide_amount: Yup.boolean(),
  contact: Yup.object({
    email: Yup.string().email(i18n.t('Invalid email address')),
    phone_number: Yup.string()
      .matches(/^\d{8,9}$/, i18n.t('Invalid phone number'))
      .when('anonymous', {
        is: false,
        then: (s) => s.required(i18n.t('At least the last name is required')),
      }),
  }),
  full_name: Yup.object({
    last_name: Yup.string().when('anonymous', {
      is: false,
      then: (s) => s.required(i18n.t('At least the last name is required')),
    }),
    first_name: Yup.string(),
  }),
  invitation: Yup.string().nullable(),
});

type FundContributeValues = Yup.InferType<typeof formSchema>;

let socket: TrxSocket;
const systemMode = ThemeModeComponent.getSystemMode() as 'light' | 'dark';

function FundDonate({ fund }: { fund: FundModel }) {
  const { mode } = useThemeMode();
  const calculatedMode = mode === 'system' ? systemMode : mode;

  const requestId = useRef(random());
  const [showToast, setShowToast] = useState<{
    display: boolean;
    service?: string;
  }>({ display: false });
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // Legacy note:
  const parsed = parse(location.href, true);
  const invitation: string = parsed.query.invitation;

  useEffect(() => {
    return () => {
      socket?.closeSocket();
    };
  }, []);

  const { t } = useTranslation();
  const providers = getPaymentMethod('CM');
  return (
    <KTCard flush={true} className={'w-lg-900px'}>
      <div className='card-header border-0'>
        <div className='card-title flex-column'>
          <h2 className='mb-1'>
            {t('Contribute_to_fund_title', { fund: fund.title })}
          </h2>
        </div>
      </div>
      <KTCardBody>
        <Formik<FundContributeValues>
          validationSchema={formSchema}
          initialValues={{
            anonymous: false,
            accept_terms: true,
            service: '',
            full_name: {},
            contact: {},
            payer: '',
            amount: 0,
            invitation: invitation ?? undefined,
          }}
          onSubmit={(values, { setSubmitting, setErrors }) => {
            setSubmitting(true);
            const processTrxResponse = (ret: TransactionPerformResponse) => {
              const status = ret.transaction?.status || ret.status;
              // var transaction = ret.transaction;
              if (['SUCCESS', 'FAILED'].includes(status)) {
                setSubmitting(false);
                const isSuccess = status === 'SUCCESS';

                const message = isSuccess
                  ? t('Donation_success_transaction_message', {
                      amount: ret.transaction.amount,
                      currency: ret.transaction.currency,
                    })
                  : t('Default_failure_transaction_message');

                Swal.fire({
                  title: isSuccess ? t('Confirmation!') : t('Failure!'),
                  text: message,
                  icon: isSuccess ? 'success' : 'error',
                  buttonsStyling: false,
                  confirmButtonText: t('Ok, got it!'),
                  customClass: {
                    confirmButton: 'btn fw-bold btn-light-primary',
                  },
                  background:
                    calculatedMode == 'dark' ? 'rgb(21, 23, 28)' : undefined,
                });
                socket.closeSocket();
              }
            };
            socket = new TrxSocket(requestId.current, processTrxResponse);
            if (invitation) {
              values.invitation = invitation;
            }

            axios
              .post(
                `/fund/${fund.slug}/contribute/`,
                {
                  ...values,
                  full_name: !values.full_name?.last_name
                    ? undefined
                    : values.full_name,
                  contact: !values.contact?.phone_number
                    ? undefined
                    : values.contact,
                },
                {
                  headers: {
                    'X-MeSomb-RequestID': requestId.current,
                    'X-MeSomb-OperationMode': 'asynchronous',
                  },
                }
              )
              .then(({ data }) => {
                setShowToast({ display: true, service: values.service });
                if (data.status != 'PENDING') {
                  processTrxResponse(data);
                }
                requestId.current = random();
              })
              .catch((error) => {
                setSubmitting(false);
                processErrorResponse(error, setErrors);
                requestId.current = random();
              });
          }}
        >
          {({ isSubmitting, isValid, touched, errors, values }) => {
            return (
              <Form noValidate>
                <div className='row'>
                  <div className='col-lg-6'>
                    {!fund.hide_target && (fund.target ?? 0) > 0 && (
                      <FundTarget fund={fund} className={'mb-5'} />
                    )}

                    <div className='fs-5 fw-semibold text-gray-600'>
                      {fund.category?.title} {t('for')} {fund.beneficiaries}
                    </div>

                    <div
                      className='d-flex flex-row-fluid bgi-no-repeat bgi-position-x-center bgi-size-cover card-rounded h-150px h-lg-250px mb-lg-20 me-lg-20 mt-lg-10'
                      style={{
                        backgroundImage: `url(${toAbsoluteStatic((typeof fund.profile === 'string' ? fund.profile : fund.profile?.path) ?? '')})`,
                      }}
                    ></div>

                    <div
                      className='fs-5 fw-semibold text-gray-600'
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(fund.description),
                      }}
                    ></div>
                  </div>
                  <div className='col-lg-6'>
                    <Field type={'hidden'} name={'invitation'} />
                    {/*<h3 className="mb-10 font-weight-bold text-dark">{t('Your Participation')}</h3>*/}

                    <h4 className='mb-2 mt-7'>{t('Payment Method')}</h4>
                    <FormField
                      type={'megaOption'}
                      name={'service'}
                      error={errors.service}
                      touched={touched.service}
                      options={providers.map((p) => ({
                        value: p.key,
                        label: p.service_name,
                        image: toAbsoluteStatic(p.logo),
                      }))}
                    />
                    <FormField
                      type={'phone'}
                      name={'payer'}
                      error={errors.payer}
                      touched={touched.payer}
                      placeholder={'670000000'}
                      showCountryCode={false}
                    />
                    <h4 className='mb-2 mt-7'>{t('Your Contribution')}</h4>
                    <FormField
                      type={'amount'}
                      name={'amount'}
                      error={errors.amount}
                      touched={touched.amount}
                      placeholder={t('Amount')}
                    />
                    <FormField
                      type={'switch'}
                      name={'anonymous'}
                      error={errors.anonymous}
                      touched={touched.anonymous}
                      label={t('Contribute Anonymously')}
                      help={t('Your name will be hide to other participants')}
                    />
                    <FormField
                      type={'switch'}
                      name={'hide_amount'}
                      error={errors.hide_amount}
                      touched={touched.hide_amount}
                      label={t('Hide Amount')}
                      help={t('Hide the amount of my participation to others')}
                    />
                    <FormField
                      type={'switch'}
                      name={'accept_terms'}
                      error={errors.accept_terms}
                      touched={touched.accept_terms}
                      label={t('Accept terms')}
                      help={t(
                        'I have read and accept the Terms & Conditions and the Privacy Policy'
                      )}
                    />

                    {!values.anonymous && (
                      <>
                        <div className='separator separator-dashed mb-10'></div>

                        <div className='pb-5 contributor-info'>
                          <h3 className='mb-5 font-weight-bold text-dark'>
                            {t('Your Informations')}
                          </h3>
                          <div>
                            <div
                              className={clsx(
                                'form-control form-control-lg p-0 mb-4',
                                {
                                  'is-invalid':
                                    extract(touched, 'contact') &&
                                    extract(errors, 'contact'),
                                },
                                {
                                  'is-valid':
                                    extract(touched, 'contact') &&
                                    !extract(errors, 'contact'),
                                }
                              )}
                            >
                              <Field
                                id={`id_contact_email`}
                                type={'email'}
                                name={'contact.email'}
                                placeholder={'contact@example.com'}
                                className={clsx(
                                  `form-control form-control-transparent`
                                  // {'is-invalid': extract(touched, name) && extract(errors, name)},
                                  // {
                                  //   'is-valid': extract(touched, name) && !extract(errors, name),
                                  // }
                                )}
                              />
                              <Field
                                id={`id_contact_phone_number`}
                                type={'tel'}
                                name={'contact.phone_number'}
                                placeholder={'670000000'}
                                className={clsx(
                                  `form-control form-control-transparent`
                                  // {'is-invalid': extract(touched, name) && extract(errors, name)},
                                  // {
                                  //   'is-valid': extract(touched, name) && !extract(errors, name),
                                  // }
                                )}
                              />
                            </div>
                            <FormField
                              type={'full_name'}
                              name={'full_name'}
                              error={errors.full_name}
                              touched={touched.full_name}
                              label={t('Full Name')}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className='d-flex justify-content-end py-6 px-9'>
                      <button type='reset' className='btn btn-light me-3'>
                        {t('Discard')}
                      </button>
                      <ButtonIndicator
                        type={'submit'}
                        label={`${t('I Participate')} ${
                          values.amount
                            ? t('intlCurrency', {
                                value: values.amount,
                                currency: fund.currency ?? 'XAF',
                              })
                            : ''
                        }`.trim()}
                        loading={isSubmitting}
                        disabled={isSubmitting || !isValid || !touched}
                        marginTop={0}
                        data-cy={'submit-button'}
                      />
                    </div>
                    {showToast.display && (
                      <Toast
                        onClose={() => setShowToast({ display: false })}
                        show={showToast.display}
                        delay={5000}
                        autohide={true}
                        bg={'light'}
                        animation={true}
                        style={{ width: '100%' }}
                        className={'mt-2'}
                      >
                        <Toast.Body className={'p-2 fs-5'}>
                          {t('Check_phone_validation', {
                            ussd: { ORANGE: '#150*50#', MTN: '*126#' }[
                              showToast.service!
                            ],
                          })}
                        </Toast.Body>
                      </Toast>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
        <Helmet>
          <title>{t('Contribute_to_fund_title', { fund: fund.title })}</title>
        </Helmet>
      </KTCardBody>
    </KTCard>
  );
}

export default FundDonate;
